import { useState, useEffect, useRef, useMemo, useCallback } from "react"
import { connect } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimes } from "@fortawesome/free-solid-svg-icons"
import { Plus, Send, Search, Package, Trash2, Eye, Check } from "lucide-react"
import { ReactComponent as MaterialsEmptyState } from "../../../assets/svg/materials-empty-state.svg"
import AddCustomMaterialPopup from "./AddCustomMaterialPopup.tsx"
import SendQuotePopup from "./SendQuotePopup.tsx"
import MaterialsListPreview from "./MaterialsListPreview.tsx"
import styles from "./CalculadoraDeMateriales.module.scss"
import CustomSelect from "../../../pages/ProjectDocumentation/components/CustomSelect.tsx"
import DownloadPDFButton from "./DownloadPdfButton"
import { getCategoriesWithMaterials } from "../../../store/actions/categoryActions"
import {
  closeMainModal,
  editSubquotationMaterials,
  getMaterialsBySubquotationId,
  openMainModal,
} from "../../../store/actions/profileActions"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { motion } from "framer-motion"
import CalculadoraDeMaterialesSkeleton from "./CalculadoraDeMaterialesSkeleton"
import { fireAnalytics } from "../../../utils/firebase"
import { useActionGuard } from "../../../hooks/useActionGuard.ts"

interface CatalogMaterial {
  id: number | string
  name: string
  unit?: string
  price?: number
}

interface CategoryFromApi {
  id: number | string
  name: string
  materials?: CatalogMaterial[]
  jurisdiction?: unknown
  currency?: string
}

interface CategoriesResponse {
  data?: CategoryFromApi[]
}

interface SelectedMaterial {
  id: number | string
  name: string
  unit: string
  quantity: number
  category: string
  price?: number
  temporary?: boolean
  currency?: string
}

interface SubquotationCategory {
  name: string
  materials: Array<{ id: number | string; name: string; unit?: string; amount?: number }>
}

interface QuotationData {
  quotation?: { name?: string; user?: { id: number | string } }
  subquotation?: Array<{ id: number | string }>
}

interface UserData {
  id: number | string
}

interface CalculadoraReduxState {
  category: { categories: CategoriesResponse }
  profile: {
    quotation: QuotationData | null
    materialsBySubquotation: Record<string, SubquotationCategory[]> | null
  }
  general: { userData: UserData | null }
}

interface CalculadoraDispatchProps {
  getCategoriesWithMaterials: () => Promise<void>
  getMaterialsBySubquotationId: (subquotationId: number | string) => Promise<void>
  editSubquotationMaterials: (
    subquotationId: number | string,
    materials: Array<{
      category_id: number
      material_id: number | string
      temporary: boolean
      amount: number
      name: string
      unit: string
    }>,
    navigate: (path: string) => void
  ) => Promise<void>
  openMainModal: (config: {
    title: string
    body: string
    iconType: string
    noLabel: string
    yesLabel: string
    confirmHandler: () => void
    customCloseMainModal: () => void
  }) => void
  closeMainModal: () => void
}

type CalculadoraProps = CalculadoraDispatchProps & {
  categoriesWithMaterials: CategoriesResponse
  quotationData: QuotationData | null
  materialsBySubquotation: Record<string, SubquotationCategory[]> | null
  userData: UserData | null
}

const ulVariants = {
  hidden: {
    opacity: 0,
    height: 0,
  },
  visible: {
    opacity: 1,
    height: "auto",
    transition: {
      opacity: {
        duration: 0.8,
      },
      height: {
        duration: 0.3,
      },
    },
  },
}

const CalculadoraDeMateriales = ({
  getCategoriesWithMaterials,
  categoriesWithMaterials,
  quotationData,
  materialsBySubquotation,
  openMainModal,
  userData,
  getMaterialsBySubquotationId,
  closeMainModal,
  editSubquotationMaterials,
}: CalculadoraProps) => {
  const { id } = useParams<{ id?: string }>()
  const location = useLocation()
  const searchInputRef = useRef<HTMLInputElement>(null)
  const { guard } = useActionGuard()
  const isEstimacionDeCostos = location.pathname.includes("estimacion-costos")

  const [categories, setCategories] = useState<CategoriesResponse>(categoriesWithMaterials)
  const [selectedMaterials, setSelectedMaterials] = useState<SelectedMaterial[]>([])
  const [expandedCategories, setExpandedCategories] = useState<string[]>([])
  const [selectedCategory, setSelectedCategory] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  const [filteredMaterials, setFilteredMaterials] = useState<(CatalogMaterial & { category: string })[]>([])
  const [showAddCustomPopup, setShowAddCustomPopup] = useState(false)
  const [showSendQuotePopup, setShowSendQuotePopup] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [isSyncingMaterials, setIsSyncingMaterials] = useState(true)
  const [originalMaterials, setOriginalMaterials] = useState<SelectedMaterial[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isEditMode, setIsEditMode] = useState(!!id)
  const [searchInputActive, setSearchInputActive] = useState(false)
  const [showMaterialAddedToast, setShowMaterialAddedToast] = useState(false)
  const prevMaterialsCountRef = useRef(0)
  const [searchRequested, setSearchRequested] = useState(false)
  const [searchResultsExpanded, setSearchResultsExpanded] = useState(false)
  const [searchHighlightIndex, setSearchHighlightIndex] = useState(-1)
  const [lastAddedMaterialId, setLastAddedMaterialId] = useState<number | string | null>(null)
  const SEARCH_RESULTS_LIMIT = 15

  const categoryOptions = useMemo(() => {
    const sortedCategories =
      categories?.data
        ?.slice()
        .sort((a, b) => (a?.name ?? "").localeCompare(b?.name ?? ""))
        ?.map((category) => ({
          value: String(category.id),
          label: category.name,
        })) ?? []

    return [
      {
        value: "custom",
        label: "Crear material personalizado",
        description: "Configura materiales a medida para tu proyecto",
        variant: "accent" as const,
      },
      ...sortedCategories,
    ]
  }, [categories])

  const navigate = useNavigate()

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      await getCategoriesWithMaterials()
      setIsLoading(false)
    }
    fetchData()
    if (userData?.id) {
      fireAnalytics("create_quotation", { action: "create_quotation", user_info: userData.id })
    }
  }, [])

  useEffect(() => {
    setCategories(categoriesWithMaterials)
  }, [categoriesWithMaterials])

  useEffect(() => {
    setIsEditMode(!!id)
    setSelectedMaterials([])
  }, [id])

  useEffect(() => {
    if (isEditMode && quotationData && isSyncingMaterials) {
      const fetchMaterials = async () => {
        try {
          setIsLoading(true)
          const subId = quotationData?.subquotation?.[0]?.id
          if (subId != null) {
            await getMaterialsBySubquotationId(subId)
          }

          if (!materialsBySubquotation) {
            return
          }

          const subCotizaciones = Object.values(materialsBySubquotation)
          const formattedMaterials: SelectedMaterial[] = subCotizaciones.flatMap((subCotizacion) =>
            subCotizacion.flatMap((category) =>
              (category.materials || []).map((material) => ({
                id: material.id,
                name: material.name,
                unit: material.unit ?? "N/A",
                quantity: material.amount ?? 1,
                category: category.name ?? "Sin Categoría",
              }))
            )
          )

          setSelectedMaterials(formattedMaterials)
        } catch (error) {
          console.error("Error al obtener materiales:", error)
        } finally {
          setIsSyncingMaterials(false)
          setIsLoading(false)
        }
      }

      fetchMaterials()
    }
  }, [isEditMode, quotationData, isSyncingMaterials])

  useEffect(() => {
    if (isEditMode && materialsBySubquotation) {
      const subCotizaciones = Object.values(materialsBySubquotation)
      const formattedMaterials: SelectedMaterial[] = subCotizaciones.flatMap((subCotizacion) =>
        subCotizacion.flatMap((category) =>
          (category.materials || []).map((material) => ({
            id: material.id,
            name: material.name,
            unit: material.unit ?? "N/A",
            quantity: material.amount ?? 1,
            category: category.name ?? "Sin Categoría",
          }))
        )
      )
      setOriginalMaterials(formattedMaterials)
    }
  }, [materialsBySubquotation, isEditMode])

  useEffect(() => {
    const count = selectedMaterials.length
    const prev = prevMaterialsCountRef.current
    if (prev + 1 === count && count > 0) {
      setShowMaterialAddedToast(true)
    }
    prevMaterialsCountRef.current = count
  }, [selectedMaterials.length])

  useEffect(() => {
    if (!showMaterialAddedToast) return
    const timer = setTimeout(() => setShowMaterialAddedToast(false), 3000)
    return () => clearTimeout(timer)
  }, [showMaterialAddedToast])

  useEffect(() => {
    if (lastAddedMaterialId == null) return
    const scrollToAdded = () => {
      const el = document.querySelector(`[data-summary-material-id="${lastAddedMaterialId}"]`)
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "nearest" })
      }
    }
    const raf = requestAnimationFrame(() => {
      scrollToAdded()
    })
    const clearHighlight = setTimeout(() => setLastAddedMaterialId(null), 2200)
    return () => {
      cancelAnimationFrame(raf)
      clearTimeout(clearHighlight)
    }
  }, [lastAddedMaterialId])

  const hasChanges = (): boolean => {
    if (selectedMaterials.length !== originalMaterials.length) {
      return true
    }

    return selectedMaterials.some((material, index) => {
      const originalMaterial = originalMaterials[index]
      if (!originalMaterial) return true
      return material.id !== originalMaterial.id || Number(material.quantity) !== Number(originalMaterial.quantity)
    })
  }

  const removeMaterial = (materialId: number | string) => {
    setSelectedMaterials((prev) => prev.filter((m) => m.id !== materialId))
  }

  useEffect(() => {
    if (!searchTerm) {
      setFilteredMaterials([])
      return
    }

    const removeAccents = (str: string) => {
      return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    }

    const normalizedSearchTerm = removeAccents(searchTerm.toLowerCase())

    const filtered = (categories.data ?? []).flatMap((category) => {
      return (category.materials ?? [])
        .filter((material) => {
          const normalizedMaterialName = removeAccents(material.name.toLowerCase())
          return normalizedMaterialName.includes(normalizedSearchTerm)
        })
        .map((material) => ({
          ...material,
          category: category.name,
        }))
    })

    setFilteredMaterials(filtered)
  }, [searchTerm, categories])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    setSearchResultsExpanded(false)
    setSearchHighlightIndex(-1)
  }

  const visibleMaterials = useMemo(() => {
    if (!searchResultsExpanded && filteredMaterials.length > SEARCH_RESULTS_LIMIT) {
      return filteredMaterials.slice(0, SEARCH_RESULTS_LIMIT)
    }
    return filteredMaterials
  }, [filteredMaterials, searchResultsExpanded])

  const hasMoreResults = filteredMaterials.length > SEARCH_RESULTS_LIMIT
  const totalItems =
    filteredMaterials.length > 0
      ? visibleMaterials.length + (hasMoreResults && !searchResultsExpanded ? 1 : 0)
      : 1

  const toggleMaterial = useCallback(
    (material: CatalogMaterial & { category?: string }, categoryId: string) => {
      const matchedCategory = categories?.data?.find((cat) => String(cat.id) === String(categoryId))
      const category = matchedCategory?.name ?? categoryId

      setSelectedMaterials((prev) => {
        const existingIndex = prev.findIndex((m) => m.id === material.id)
        if (existingIndex >= 0) {
          return prev.filter((m) => m.id !== material.id)
        }
        setLastAddedMaterialId(material.id)
        return [...prev, { ...material, quantity: 1, category, unit: material.unit ?? "N/A" } as SelectedMaterial]
      })
      setSearchTerm("")
    },
    [categories]
  )

  useEffect(() => {
    if (!searchTerm) {
      setSearchHighlightIndex(-1)
      return
    }
    setSearchHighlightIndex(0)
  }, [searchTerm, filteredMaterials.length])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!searchTerm) return
      const isNoResults = filteredMaterials.length === 0
      const itemCount = isNoResults ? 1 : totalItems

      if (e.key === "ArrowDown") {
        e.preventDefault()
        setSearchHighlightIndex((prev) => (prev < itemCount - 1 ? prev + 1 : prev))
      } else if (e.key === "ArrowUp") {
        e.preventDefault()
        setSearchHighlightIndex((prev) => (prev > 0 ? prev - 1 : -1))
      } else if (e.key === "Enter" && searchHighlightIndex >= 0) {
        e.preventDefault()
        if (isNoResults) {
          setShowAddCustomPopup(true)
          setSearchTerm("")
        } else if (searchHighlightIndex < visibleMaterials.length) {
          const material = visibleMaterials[searchHighlightIndex]
          toggleMaterial(material, material.category)
          setSearchTerm("")
        } else if (hasMoreResults && !searchResultsExpanded) {
          setSearchResultsExpanded(true)
          setSearchHighlightIndex(0)
        }
      } else if (e.key === "Escape") {
        setSearchTerm("")
        searchInputRef.current?.blur()
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [
    searchTerm,
    searchHighlightIndex,
    totalItems,
    visibleMaterials,
    filteredMaterials.length,
    hasMoreResults,
    searchResultsExpanded,
    toggleMaterial,
  ])

  const toggleCategory = (categoryId: string) => {
    if (categoryId === "custom") {
      setShowAddCustomPopup(true)
      return
    }

    if (!categoryId) {
      setSelectedCategory("")
      setExpandedCategories([])
      return
    }

    if (categoryId === selectedCategory) {
      setSelectedCategory("")
      setExpandedCategories([])
      return
    }

    setSelectedCategory(categoryId)
    setExpandedCategories((prevCategories) => (prevCategories.includes(categoryId) ? [] : [categoryId]))
  }

  const updateQuantity = (materialId: number | string, quantity: number) => {
    setSelectedMaterials((prev) => prev.map((m) => (m.id === materialId ? { ...m, quantity } : m)))
  }

  const addCustomMaterial = (newMaterial: SelectedMaterial & { categoryId?: string; category?: string }) => {
    if (!Array.isArray(categories.data)) {
      return
    }

    const categoryId = newMaterial.categoryId ?? categories.data.find((c) => c.name === newMaterial.category)?.id

    if (categoryId != null && categories.data.findIndex((c) => String(c.id) === String(categoryId)) !== -1) {
      setCategories((prevCategories) => {
        const newCategories = { ...prevCategories }
        newCategories.data = [...(prevCategories.data ?? [])]
        return newCategories
      })
    } else {
      setCategories((prevCategories) => ({
        ...prevCategories,
        data: [
          ...(prevCategories.data ?? []),
          {
            id: newMaterial.categoryId ?? "custom",
            name: newMaterial.category ?? "Personalizado",
            materials: [{ ...newMaterial, temporary: true } as CatalogMaterial],
            jurisdiction: (newMaterial as { jurisdiction?: unknown }).jurisdiction,
            currency: newMaterial.currency,
          },
        ],
      }))
    }

    const addedMaterial = {
      ...newMaterial,
      category:
        categories?.data?.find((c) => c.id === (newMaterial.categoryId ?? newMaterial.category))?.name ??
        newMaterial.category ??
        "Sin Categoría",
      unit: newMaterial.unit,
      quantity: newMaterial.quantity ?? 1,
      temporary: true,
      currency: newMaterial.currency,
    } as SelectedMaterial
    setSelectedMaterials((prevSelected) => [...prevSelected, addedMaterial])
    setLastAddedMaterialId(addedMaterial.id)

    setShowAddCustomPopup(false)
  }

  const total = selectedMaterials?.reduce((sum, m) => sum + (m.price ?? 0) * m.quantity, 0) ?? 0

  const projectName = quotationData?.quotation?.name
  const headerTitle = isEditMode
    ? "Editar cotización"
    : isEstimacionDeCostos
      ? "Crear estimación de costos"
      : "Publicá tu lista y recibí presupuestos"
  const headerSubtitle = isEstimacionDeCostos
    ? projectName
      ? `Mantén el presupuesto actualizado para ${projectName}. Más de 2800 materiales disponibles.`
      : "Controla los costos estimados de tu proyecto. Más de 2800 materiales disponibles."
    : isEditMode
      ? projectName
        ? `Actualizá los materiales para ${projectName}`
        : "Actualizá la lista de materiales antes de enviar"
      : "Armá el listado y recibí ofertas de proveedores de tu zona"
  const groupedMaterials = selectedMaterials.reduce<Record<string, SelectedMaterial[]>>((acc, material) => {
    if (!acc[material.category]) {
      acc[material.category] = []
    }
    acc[material.category].push(material)
    return acc
  }, {})

  const formattedTotal = new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
  }).format(total)

  const handleSendQuoteClick = guard(
    "send_quotation",
    async () => {
      if (isEditMode) {
        const confirmation = await new Promise<boolean>((resolve) => {
          openMainModal({
            title: "¿Estás seguro de que deseas guardar los cambios en la cotización?",
            body: "Se le notificará a los proveedores que se han realizado cambios en la cotización.",
            iconType: "warning",
            noLabel: "Cancelar",
            yesLabel: "Guardar cambios",
            confirmHandler: () => {
              closeMainModal()
              setIsLoading(true)
              resolve(true)
            },
            customCloseMainModal: () => {
              closeMainModal()
              resolve(false)
            },
          })
        })

        if (confirmation && quotationData?.subquotation?.[0]?.id != null) {
          const formattedMaterials = selectedMaterials.map((material) => {
            const category = categories?.data?.find((cat) => cat.name === material.category)
            return {
              category_id: category?.id ?? 0,
              material_id: material.id,
              temporary: material.temporary ?? false,
              amount: material.quantity,
              name: material.name,
              unit: material.unit,
            }
          })

          try {
            await editSubquotationMaterials(
              quotationData.subquotation[0].id,
              formattedMaterials.map((m) => ({
                ...m,
                category_id: Number(m.category_id) || 0,
              })),
              navigate
            )
          } catch (error) {
            console.error("Error al editar los materiales:", error)
          } finally {
            setIsLoading(false)
          }
        }
      } else {
        setShowSendQuotePopup(true)
      }
    },
    () => {
      if (isEditMode && quotationData?.quotation?.user?.id) {
        fireAnalytics("edit_quotation", { edify_user_id: quotationData.quotation!.user!.id })
      } else if (quotationData?.quotation?.user?.id) {
        fireAnalytics("send_quotation_step1", { edify_user_id: quotationData.quotation!.user!.id })
      }
    }
  )

  const handleQuoteSuccess = () => {
    setSelectedMaterials([])
  }

  const clearSearch = () => {
    setSearchTerm("")
    setFilteredMaterials([])
    setSearchResultsExpanded(false)
    setSearchHighlightIndex(-1)
  }

  const handleSearchLinkClick = () => {
    setSearchRequested(true)
    setSearchInputActive(true)
    setTimeout(() => {
      searchInputRef.current?.focus()
      searchInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
    }, 100)
  }

  const titleWhenEmpty = isEstimacionDeCostos
    ? selectedMaterials.length > 0
      ? "Para continuar selecciona una categoría"
      : "Comienza tu estimación"
    : selectedMaterials.length > 0
      ? "Para continuar selecciona una categoría"
      : "Publicá tu lista y recibí presupuestos de proveedores"

  const subTitleWhenEmpty = isEstimacionDeCostos
    ? selectedMaterials.length > 0
      ? "Selecciona una categoría o busca materiales específicos para continuar con tu estimación de costos."
      : "Elegí una categoría para empezar."
    : selectedMaterials.length > 0
      ? "Selecciona una categoría o busca materiales específicos para continuar con tu cotización."
      : "Elegí una categoría para empezar."

  const unitToShow = (unit: string): string => {
    switch (unit) {
      case "m2":
        return "Metro cuadrado"
      case "m3":
        return "Metro cúbico"
      case "kg":
        return "Kilogramo"
      case "ml":
        return "Mililitro"
      case "cm":
        return "Centímetro"
      case "u":
        return "Unidad"
      case "l":
        return "Litro"
      case "cm2":
        return "Centímetro cuadrado"
      case "cm3":
        return "Centímetro cúbico"
      case "gr":
        return "Gramo"
      case "m":
        return "Metro"
      default:
        return unit
    }
  }

  const handlePreviewButtonClick = () => {
    setShowPreview(true)
  }

  const handleCreateCustomMaterialFromList = () => {
    setShowAddCustomPopup(true)
  }

  return (
    <>
      {isLoading ? (
        <CalculadoraDeMaterialesSkeleton />
      ) : (
        <div className={styles.container}>
          <div className={styles.container_header}>
            <div className={styles.container_header_title}>
              <div className={styles.container_header_title_heading}>
                <h1 className={styles.container_header_title_text}>{headerTitle}</h1>
                {headerSubtitle && (
                  <p className={styles.container_header_title_subtitle}>{headerSubtitle}</p>
                )}
              </div>
            </div>
            <div className={styles.container_header_buttons}>
              {isEstimacionDeCostos ? (
                <DownloadPDFButton
                  userData={userData}
                  materials={selectedMaterials}
                  setShowSendQuotePopup={setShowSendQuotePopup}
                  className={styles.container_header_download}
                />
              ) : (
                <button
                  className={styles.container_header_quoteBtn}
                  onClick={handleSendQuoteClick}
                  disabled={isEditMode ? !hasChanges() : selectedMaterials.length === 0}
                >
                  <Send size={16} style={{ minHeight: "1.6rem", minWidth: "1.6rem" }} />
                  <span>{isEditMode ? "Guardar cambios" : "Enviar para cotizar"}</span>
                </button>
              )}
            </div>
          </div>
          <div className={styles.container_bodyContainer}>
            <div className={styles.container_bodyColumn}>
              {!(selectedCategory || selectedMaterials.length > 0 || searchRequested) && (
                <div className={styles.container_body_headerSpacer} aria-hidden />
              )}
              {(selectedCategory || selectedMaterials.length > 0 || searchRequested) && (
                <div className={styles.container_body_header}>
                  <div className={styles.container_body_header_inputs}>
                    <div className={styles.container_body_header_categories}>
                      <div className={styles.container_body_header_categories_dropdown}>
                        <CustomSelect
                          value={selectedCategory}
                          onChange={toggleCategory}
                          options={categoryOptions}
                          placeholder="Seleccionar categoría"
                          searchable
                          searchPlaceholder="Buscar categoría..."
                        />
                      </div>
                    </div>
                    <div
                      className={`${styles.container_body_header_searchContainer} ${
                        searchInputActive && styles.container_body_header_searchContainer_active
                      }`}
                    >
                      <div className={styles.container_body_header_searchContainer_icon}>
                        <Search size={20} />
                        <input
                          ref={searchInputRef}
                          type="text"
                          placeholder="Buscar material por nombre"
                          value={searchTerm}
                          onChange={handleSearchChange}
                          className={styles.container_body_header_searchContainer_input}
                        />
                      </div>
                      {searchTerm && (
                        <FontAwesomeIcon icon={faTimes} className={styles.clearIcon} onClick={clearSearch} />
                      )}
                      {searchTerm && filteredMaterials.length > 0 && (
                        <ul className={styles.container_body_header_searchContainer_filteredResults}>
                          {visibleMaterials.map((material, index) => (
                            <li
                              className={`${styles.container_body_header_searchContainer_filteredResults_item} ${index === searchHighlightIndex ? styles.container_body_header_searchContainer_filteredResults_item_highlighted : ""}`}
                              key={material.id}
                              onClick={() => toggleMaterial(material, material.category)}
                              onMouseEnter={() => setSearchHighlightIndex(index)}
                            >
                              <span
                                className={styles.container_body_header_searchContainer_filteredResults_item_material}
                              >
                                {material.name}
                              </span>
                              <span
                                className={styles.container_body_header_searchContainer_filteredResults_item_category}
                              >
                                {material.category}
                              </span>
                            </li>
                          ))}
                          {hasMoreResults && !searchResultsExpanded && (
                            <li
                              className={`${styles.container_body_header_searchContainer_filteredResults_item} ${styles.container_body_header_searchContainer_filteredResults_item_verMas} ${visibleMaterials.length === searchHighlightIndex ? styles.container_body_header_searchContainer_filteredResults_item_highlighted : ""}`}
                              onClick={() => setSearchResultsExpanded(true)}
                              onMouseEnter={() => setSearchHighlightIndex(visibleMaterials.length)}
                            >
                              <span className={styles.container_body_header_searchContainer_filteredResults_item_material}>
                                Ver más ({filteredMaterials.length - SEARCH_RESULTS_LIMIT} restantes)
                              </span>
                            </li>
                          )}
                        </ul>
                      )}
                      {searchTerm && filteredMaterials.length === 0 && (
                        <ul className={styles.container_body_header_searchContainer_filteredResults}>
                          <li
                            className={`${styles.container_body_header_searchContainer_filteredResults_item} ${styles.container_body_header_searchContainer_filteredResults_item_create} ${searchHighlightIndex === 0 ? styles.container_body_header_searchContainer_filteredResults_item_highlighted : ""}`}
                            onClick={() => {
                              setShowAddCustomPopup(true)
                              setSearchTerm("")
                            }}
                            onMouseEnter={() => setSearchHighlightIndex(0)}
                          >
                            <Plus
                              size={16}
                              className={styles.container_body_header_searchContainer_filteredResults_item_icon}
                            />
                            <div className={styles.container_body_header_searchContainer_filteredResults_item_createContent}>
                              <span className={styles.container_body_header_searchContainer_filteredResults_item_material}>
                                Crear material personalizado
                              </span>
                              <span className={styles.container_body_header_searchContainer_filteredResults_item_category}>
                                Agregá un material que no esté en la lista
                              </span>
                            </div>
                          </li>
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div className={styles.container_body}>
                <div className={styles.container_body_categoryMaterials}>
                  {expandedCategories && selectedCategory && expandedCategories.includes(selectedCategory) ? (
                    <motion.ul
                      variants={ulVariants}
                      initial="hidden"
                      animate="visible"
                      className={styles.container_body_categoryMaterials_list}
                    >
                      <li
                        className={`${styles.container_body_categoryMaterials_list_item} ${styles.customMaterialListItem}`}
                        onClick={handleCreateCustomMaterialFromList}
                        style={{ cursor: "pointer" }}
                      >
                        <div className={styles.container_body_categoryMaterials_list_item_text}>
                          <span className={styles.container_body_categoryMaterials_list_item_material}>
                            Crear material personalizado
                          </span>
                          <span className={styles.container_body_categoryMaterials_list_item_category}>
                            Agrega un material que no esté en la lista
                          </span>
                        </div>
                        <div className={styles.container_body_categoryMaterials_list_item_buttonContainer}>
                          <button
                            className={`${styles.container_body_categoryMaterials_list_item_button} ${styles.customMaterialButton}`}
                          >
                            Crear
                          </button>
                        </div>
                      </li>
                      {categories?.data
                        ?.find((cat) => String(cat.id) === selectedCategory)
                        ?.materials?.map((material) => {
                          const isChecked = selectedMaterials.some((m) => m.id === material.id)
                          const category =
                            categories?.data?.find((cat) => String(cat.id) === selectedCategory)?.name ?? selectedCategory
                          return (
                            <li
                              key={material.id}
                              className={styles.container_body_categoryMaterials_list_item}
                              onClick={() => toggleMaterial(material, selectedCategory)}
                              style={{ cursor: "pointer" }}
                            >
                              <div className={styles.container_body_categoryMaterials_list_item_text}>
                                <span className={styles.container_body_categoryMaterials_list_item_material}>
                                  {material.name}
                                </span>
                                <span className={styles.container_body_categoryMaterials_list_item_category}>
                                  {category}
                                </span>
                              </div>
                              <div
                                className={`${styles.container_body_categoryMaterials_list_item_buttonContainer} 
                            ${!isEstimacionDeCostos && styles.container_body_categoryMaterials_list_item_buttonContainer_estimacionDeCostos}`}
                              >
                                <div className={styles.container_body_categoryMaterials_list_item_quantity}>
                                  <div className={styles.container_body_categoryMaterials_list_item_meta}>
                                    {isEstimacionDeCostos && material.price != null && (
                                      <span>
                                        {new Intl.NumberFormat("es-AR", {
                                          style: "currency",
                                          currency: "ARS",
                                        }).format(material.price)}
                                      </span>
                                    )}
                                    <span>{material.unit}</span>
                                  </div>
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    toggleMaterial(material, selectedCategory)
                                  }}
                                  className={`${styles.container_body_categoryMaterials_list_item_button} 
                                ${isChecked && styles.container_body_categoryMaterials_list_item_button_checked}`}
                                >
                                  {isChecked ? <Trash2 size={16} /> : <Plus size={16} />}
                                </button>
                              </div>
                            </li>
                          )
                        })}
                    </motion.ul>
                  ) : (
                    <div className={styles.container_body_categoryMaterials_empty}>
                      <div className={styles.container_body_categoryMaterials_empty_visual}>
                        <MaterialsEmptyState
                          className={styles.container_body_categoryMaterials_empty_icon}
                          aria-hidden
                        />
                      </div>
                      <div className={styles.container_body_categoryMaterials_empty_content}>
                        <h2 className={styles.container_body_categoryMaterials_empty_title}>{titleWhenEmpty}</h2>
                        <p className={styles.container_body_categoryMaterials_empty_text}>{subTitleWhenEmpty}</p>
                        <div className={styles.container_body_categoryMaterials_empty_dropdown}>
                          <CustomSelect
                            value={selectedCategory}
                            onChange={toggleCategory}
                            options={categoryOptions}
                            placeholder="Elegí una categoría para empezar"
                            placement="top"
                            searchable
                            searchPlaceholder="Buscar categoría..."
                          />
                        </div>
                        <div className={styles.container_body_categoryMaterials_empty_actions}>
                          <button
                            type="button"
                            className={styles.container_body_categoryMaterials_empty_searchLink}
                            onClick={handleSearchLinkClick}
                          >
                            ¿Sabés qué necesitás? Buscar por nombre
                          </button>
                          <button
                            type="button"
                            className={`${styles.container_body_categoryMaterials_empty_searchBtn} ${styles.customMaterialEmptyBtn}`}
                            onClick={() => setShowAddCustomPopup(true)}
                          >
                            <Plus size={16} style={{ minHeight: "1.6rem", minWidth: "1.6rem" }} />
                            Crear material personalizado
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          <div className={styles.container_summaryColumn}>
            <div className={styles.container_summary_header}>
              <h2 className={styles.container_summary_title}>Resumen de materiales</h2>
              <button
                className={styles.container_summary_showPreviewBtn}
                onClick={handlePreviewButtonClick}
                title="Vista previa de materiales"
              >
                <Eye size={20} style={{ minWidth: "2rem", minHeight: "2rem" }} />
              </button>
            </div>
            <div className={styles.container_summary}>
              {selectedMaterials && selectedMaterials.length > 0 ? (
                <div className={styles.container_summary_info}>
                  {Object.entries(groupedMaterials)
                .sort(([a], [b]) => a.localeCompare(b, "es"))
                .map(([category, materials]) => {
                    const categoryTotalQuantity = materials.reduce(
                      (sum, item) => sum + (Number(item.quantity) || 0),
                      0
                    )
                    const categoryTotalPrice = isEstimacionDeCostos
                      ? materials.reduce(
                          (sum, item) =>
                            sum + (Number(item.price) || 0) * (Number(item.quantity) || 0),
                          0
                        )
                      : null

                    return (
                      <div key={category} className={styles.container_summary_info_category}>
                        <div className={styles.container_summary_info_category_header}>
                          <div className={styles.container_summary_info_category_heading}>
                            <h3 className={styles.container_summary_info_category_title}>
                              {category || "Sin categoría"}
                            </h3>
                            <div className={styles.container_summary_info_category_subtitle}>
                              <span>
                                {categoryTotalQuantity} {categoryTotalQuantity === 1 ? "material" : "materiales"}
                              </span>
                              {categoryTotalPrice !== null && (
                                <span>
                                  {new Intl.NumberFormat("es-AR", {
                                    style: "currency",
                                    currency: "ARS",
                                  }).format(categoryTotalPrice)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className={styles.container_summary_info_category_items}>
                          {materials.map((material) => {
                            const subtotal =
                              isEstimacionDeCostos && material.price != null
                                ? (Number(material.price) || 0) * (Number(material.quantity) || 0)
                                : null
                            return (
                              <div
                                key={material.id}
                                data-summary-material-id={material.id}
                                className={`${styles.container_summary_info_category_item} ${lastAddedMaterialId === material.id ? styles.container_summary_info_category_item_highlighted : ""}`}
                              >
                                <div className={styles.container_summary_info_category_item_header}>
                                  <span className={styles.container_summary_info_category_item_name}>
                                    {material.name}
                                  </span>
                                  <span className={styles.container_summary_info_category_item_badge}>
                                    {unitToShow(material.unit)}
                                  </span>
                                </div>
                                {subtotal !== null && (
                                  <div className={styles.container_summary_info_category_item_price}>
                                    {new Intl.NumberFormat("es-AR", {
                                      style: "currency",
                                      currency: "ARS",
                                    }).format(subtotal)}
                                  </div>
                                )}
                                <div className={styles.container_summary_info_category_item_actions}>
                                  <div className={styles.container_summary_info_category_item_quantity}>
                                    <span className={styles.container_summary_info_category_item_quantityLabel}>
                                      Cantidad
                                    </span>
                                    <input
                                      type="number"
                                      min={1}
                                      className={styles.container_summary_info_category_quantityInput}
                                      value={material.quantity}
                                      onChange={(e) =>
                                        updateQuantity(
                                          material.id,
                                          Number.parseInt(e.target.value, 10) || material.quantity
                                        )
                                      }
                                    />
                                  </div>
                                  <button
                                    onClick={() => removeMaterial(material.id)}
                                    className={styles.container_summary_info_category_actions_removeBtn}
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className={styles.container_summary_empty}>
                  <Package size={64} className={styles.container_summary_empty_icon} />
                  <h2 className={styles.container_summary_empty_title}>No hay materiales seleccionados</h2>
                  <p className={styles.container_summary_empty_text}>
                    Agregá materiales desde el panel izquierdo para ver el resumen
                  </p>
                </div>
              )}
              <div className={styles.container_summary_info_category_total}>
                {isEstimacionDeCostos ? (
                  <>
                    <div className={styles.container_summary_info_category_total_text}>
                      Total estimado
                      <span className={styles.container_summary_info_category_total_price}>
                        {formattedTotal}
                      </span>
                    </div>
                    <p className={styles.container_summary_info_category_total_disclaimer}>
                      *Los precios son estimativos y pueden variar.
                    </p>
                  </>
                ) : (
                  <div className={styles.container_summary_info_category_total_text}>
                    <span className={styles.container_summary_info_category_total_materialsLabel}>
                      Materiales en la lista
                    </span>
                    <span className={styles.container_summary_info_category_total_materialsCount}>
                      {selectedMaterials.length}
                    </span>
                  </div>
                )}
                <div className={styles.container_summary_info_category_total_actions}>
                  {isEstimacionDeCostos ? (
                    <DownloadPDFButton
                      userData={userData}
                      materials={selectedMaterials}
                      setShowSendQuotePopup={setShowSendQuotePopup}
                      className={`${styles.container_header_download} ${styles.container_summary_info_category_total_downloadBtn}`}
                    />
                  ) : (
                    <button
                      className={
                        isEstimacionDeCostos
                          ? styles.container_summary_info_category_total_primaryBtn
                          : styles.container_header_quoteBtn
                      }
                      onClick={handleSendQuoteClick}
                      disabled={isEditMode ? !hasChanges() : selectedMaterials.length === 0}
                    >
                      <Send size={16} style={{ minHeight: "1.6rem", minWidth: "1.6rem" }} />
                      <span>{isEditMode ? "Guardar cambios" : "Enviar para cotizar"}</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          </div>
          {showAddCustomPopup && (
            <AddCustomMaterialPopup
              onClose={() => setShowAddCustomPopup(false)}
              onAdd={addCustomMaterial}
              categories={categories}
              isEstimacionDeCostos={isEstimacionDeCostos}
            />
          )}
          {showSendQuotePopup && (
            <SendQuotePopup
              onClose={() => setShowSendQuotePopup(false)}
              onSend={handleSendQuoteClick}
              materials={selectedMaterials}
              onSuccess={handleQuoteSuccess}
              setShowPreview={setShowPreview}
            />
          )}
          {showPreview && (
            <MaterialsListPreview
              materials={selectedMaterials}
              sendHandler={handleSendQuoteClick}
              setShowSendQuotePopup={setShowSendQuotePopup}
              isEstimacionDeCostos={isEstimacionDeCostos}
              closePreview={() => setShowPreview(false)}
            />
          )}
        </div>
      )}
      {showMaterialAddedToast && (
        <motion.div
          className={styles.materialAddedToast}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Check size={20} className={styles.materialAddedToast_icon} />
          <span>Material agregado</span>
        </motion.div>
      )}
    </>
  )
}

const mapStateToProps = (state: CalculadoraReduxState) => ({
  categoriesWithMaterials: state.category.categories,
  quotationData: state.profile.quotation,
  materialsBySubquotation: state.profile.materialsBySubquotation,
  userData: state.general.userData,
})

export default connect(mapStateToProps, {
  getCategoriesWithMaterials,
  getMaterialsBySubquotationId,
  editSubquotationMaterials,
  openMainModal,
  closeMainModal,
})(CalculadoraDeMateriales)
